/**
 * MUTAGIM.SHOP — Admin Blacklist Page (Phase 10)
 * Gestion des entrées blacklistées (email, IP, carte)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Ban, Plus, Trash2, Mail, Globe, CreditCard,
  Loader2, RefreshCw, Search, ArrowLeft
} from 'lucide-react';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminBlacklistPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [newEntry, setNewEntry] = useState({ type: 'email', value: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchBlacklist();
  }, [filter, search]);

  const fetchBlacklist = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/admin/security/blacklist`);
      if (filter) url.searchParams.set('type', filter);
      if (search) url.searchParams.set('search', search);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching blacklist:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const addToBlacklist = async () => {
    if (!newEntry.value || !newEntry.reason) {
      toast.error('Valeur et raison obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/security/blacklist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newEntry)
      });

      if (response.ok) {
        toast.success('Entrée ajoutée à la blacklist');
        setAddModal(false);
        setNewEntry({ type: 'email', value: '', reason: '' });
        fetchBlacklist();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error adding to blacklist:', error);
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const removeFromBlacklist = async (entryId) => {
    if (!window.confirm('Supprimer cette entrée de la blacklist ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/security/blacklist/${entryId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Entrée supprimée');
        fetchBlacklist();
      }
    } catch (error) {
      console.error('Error removing from blacklist:', error);
      toast.error('Erreur');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'ip': return <Globe className="w-4 h-4" />;
      case 'card': return <CreditCard className="w-4 h-4" />;
      default: return <Ban className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <AdminPageWrapper title="Blacklist" subtitle="Gestion des entrées blacklistées">
      <div className="space-y-6" data-testid="admin-blacklist-page">
        {/* Header actions */}
        <div className="flex justify-end">
          <Button
            onClick={() => setAddModal(true)}
            className="bg-mutagim-red hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" /> Ajouter
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { key: 'email', label: 'Emails', value: stats.email || 0, icon: Mail },
            { key: 'ip', label: 'Adresses IP', value: stats.ip || 0, icon: Globe },
            { key: 'card', label: 'Cartes', value: stats.card || 0, icon: CreditCard },
          ].map(stat => (
            <button
              key={stat.key}
              onClick={() => setFilter(filter === stat.key ? null : stat.key)}
              className={cn(
                "p-4 rounded-lg border transition-colors text-left",
                filter === stat.key 
                  ? "bg-gray-800 border-mutagim-red" 
                  : "bg-gray-900 border-gray-800 hover:border-gray-700"
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className="w-5 h-5 text-mutagim-red" />
                <span className="text-gray-200 text-sm">{stat.label}</span>
              </div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-200" />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-gray-900 border-gray-800 text-white"
            />
          </div>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16">
            <Ban className="w-16 h-16 mx-auto mb-4 text-gray-700" />
            <p className="text-gray-200">Aucune entrée dans la blacklist</p>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Valeur</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Raison</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-200">Blocages</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Ajouté par</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Date</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-gray-200">
                        {getTypeIcon(entry.type)}
                        <span className="text-sm capitalize">{entry.type}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white font-mono text-sm">{entry.value}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-200">{entry.reason}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn(
                        "font-bold",
                        entry.blocks_count > 0 ? "text-mutagim-red" : "text-gray-500"
                      )}>
                        {entry.blocks_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-200">{entry.created_by}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-200">{formatDate(entry.created_at)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromBlacklist(entry.id)}
                        className="text-gray-200 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal Ajouter */}
        {addModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-lg w-full max-w-md border border-gray-800">
              <div className="p-6 border-b border-gray-800">
                <h3 className="text-lg font-semibold text-white">
                  Ajouter à la blacklist
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-200 mb-2">Type</label>
                  <div className="flex gap-2">
                    {[
                      { key: 'email', label: 'Email', icon: Mail },
                      { key: 'ip', label: 'IP', icon: Globe },
                      { key: 'card', label: 'Carte', icon: CreditCard },
                    ].map(type => (
                      <button
                        key={type.key}
                        onClick={() => setNewEntry(prev => ({ ...prev, type: type.key }))}
                        className={cn(
                          "flex-1 p-3 rounded-lg border flex flex-col items-center gap-1 transition-colors",
                          newEntry.type === type.key
                            ? "bg-mutagim-red/20 border-mutagim-red text-white"
                            : "bg-gray-800 border-gray-700 text-gray-200 hover:border-gray-600"
                        )}
                      >
                        <type.icon className="w-5 h-5" />
                        <span className="text-xs">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-200 mb-2">
                    {newEntry.type === 'email' ? 'Adresse email' : 
                     newEntry.type === 'ip' ? 'Adresse IP' : 
                     'Fingerprint carte'}
                  </label>
                  <Input
                    value={newEntry.value}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, value: e.target.value }))}
                    placeholder={
                      newEntry.type === 'email' ? 'fraudeur@example.com' : 
                      newEntry.type === 'ip' ? '192.168.1.1' : 
                      'visa:4242:12:2025'
                    }
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-200 mb-2">
                    Raison <span className="text-mutagim-red">*</span>
                  </label>
                  <Textarea
                    value={newEntry.reason}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Raison du blocage (obligatoire)..."
                    className="bg-gray-800 border-gray-700 text-white"
                    rows={3}
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => { setAddModal(false); setNewEntry({ type: 'email', value: '', reason: '' }); }}
                  className="text-gray-200"
                >
                  Annuler
                </Button>
                <Button
                  onClick={addToBlacklist}
                  disabled={submitting || !newEntry.value || !newEntry.reason}
                  className="bg-mutagim-red hover:bg-red-700"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
                  Blacklister
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminPageWrapper>
  );
}

export default AdminBlacklistPage;
